import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Button from "@/components/base/Button";
import { clinicInfo, navLinks, serviceMenu } from "@/mocks/site";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Close any open menus whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  // Close the Services dropdown when clicking outside of it.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isServicesActive = location.pathname === "/services" || location.pathname.startsWith("/services/");

  const linkClasses = (isActive: boolean) =>
    [
      "relative rounded-md px-2.5 py-2 font-label text-sm font-medium whitespace-nowrap transition-colors",
      isActive ? "text-primary-600" : "text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700",
    ].join(" ");

  return (
    <>
      <div className="hidden lg:block bg-gradient-to-r from-primary-950 via-primary-900 to-primary-950 text-background-200">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 text-xs font-label">
          <div className="flex items-center gap-6">
            <a href={clinicInfo.phoneHref} className="flex items-center gap-2 hover:text-background-50">
              <i className="ri-phone-line text-sm" aria-hidden="true"></i>
              {clinicInfo.phoneDisplay}
            </a>
            <span className="flex items-center gap-2">
              <i className="ri-map-pin-line text-sm" aria-hidden="true"></i>
              {clinicInfo.addressFull}
            </span>
          </div>
          <div className="flex items-center gap-6">
            {clinicInfo.hours[0] ? (
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-sm" aria-hidden="true"></i>
                {clinicInfo.hours[0]}
              </span>
            ) : null}
            <span className="flex items-center gap-2 text-secondary-300">
              <i className="ri-verified-badge-line text-sm" aria-hidden="true"></i>
              Senior Consultant Spine Surgeon, SUHAM Hospitals
            </span>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 border-b bg-background-50/95 backdrop-blur transition-shadow duration-300 ${
          scrolled ? "border-background-200 shadow-[0_6px_24px_-16px_rgba(20,40,80,0.5)]" : "border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-3" aria-label="Ortho Spine Care home">
            <img
              src={clinicInfo.logo}
              alt="Ortho Spine Care logo, orthopaedic and spine specialists in Trichy"
              className="h-10 w-auto md:h-12"
              title="Ortho Spine Care — Orthopaedic & Spine Specialists, Trichy"
            />
          </Link>

          <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Primary">
            {navLinks.map((link) => {
              if (link.href === "/services") {
                return (
                  <div
                    key={link.label}
                    ref={dropdownRef}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => setServicesOpen((prev) => !prev)}
                      aria-expanded={servicesOpen}
                      aria-haspopup="true"
                      className={`flex cursor-pointer items-center gap-1 ${linkClasses(isServicesActive)}`}
                    >
                      {link.label}
                      <i
                        className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                          servicesOpen ? "rotate-180" : ""
                        }`}
                        aria-hidden="true"
                      ></i>
                      <span
                        className={`absolute inset-x-2.5 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-opacity duration-200 ${
                          isServicesActive ? "opacity-100" : "opacity-0"
                        }`}
                        aria-hidden="true"
                      />
                    </button>

                    {servicesOpen ? (
                      <div className="absolute left-0 top-full z-50 w-80 pt-2">
                        <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50 p-2 shadow-[0_24px_60px_-30px_rgba(15,40,80,0.5)]">
                          {serviceMenu.map((item) => (
                            <NavLink
                              key={item.href}
                              to={item.href}
                              end={item.href === "/services"}
                              className={({ isActive }) =>
                                [
                                  "flex items-start gap-3 rounded-lg px-3 py-3 transition-colors",
                                  isActive ? "bg-primary-50" : "hover:bg-secondary-50",
                                ].join(" ")
                              }
                            >
                              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                                <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                              </span>
                              <span className="min-w-0">
                                <span className="block font-heading text-sm font-semibold text-foreground-950">
                                  {item.label}
                                </span>
                                <span className="mt-0.5 block font-body text-xs leading-snug text-foreground-600">
                                  {item.text}
                                </span>
                              </span>
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              }

              return (
                <NavLink key={link.label} to={link.href} end={link.href === "/"} className={({ isActive }) => linkClasses(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span>{link.label}</span>
                      <span
                        className={`absolute inset-x-2.5 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-opacity duration-200 ${
                          isActive ? "opacity-100" : "opacity-0"
                        }`}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={clinicInfo.phoneHref}
              className="hidden items-center gap-2 rounded-md border border-primary-200 px-3 py-2 font-label text-sm font-semibold text-primary-700 transition-colors hover:bg-secondary-50 2xl:flex"
              aria-label="Call Ortho Spine Care"
            >
              <i className="ri-phone-fill" aria-hidden="true"></i>
              <span className="whitespace-nowrap">Call Now</span>
            </a>
            <Button to="/appointment" size="md" icon="ri-calendar-check-line" className="hidden sm:inline-flex">
              Book Appointment
            </Button>
            <a
              href={clinicInfo.phoneHref}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-primary-200 text-primary-700 xl:hidden"
              aria-label="Call Ortho Spine Care"
            >
              <i className="ri-phone-fill text-lg" aria-hidden="true"></i>
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 xl:hidden"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className="border-t border-background-200 bg-background-50 xl:hidden">
            <nav className="mx-auto max-w-7xl px-4 py-4 md:px-6" aria-label="Mobile">
              <ul className="grid grid-cols-2 gap-1 sm:grid-cols-3">
                {navLinks.map((link) => (
                  <li key={link.label}>
                    <NavLink
                      to={link.href}
                      end={link.href === "/"}
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) =>
                        [
                          "block rounded-md px-3 py-2.5 font-label text-sm font-medium transition-colors",
                          isActive
                            ? "bg-primary-50 text-primary-700"
                            : "text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700",
                        ].join(" ")
                      }
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>

              <div className="mt-4 border-t border-background-200 pt-4">
                <p className="font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  Services
                </p>
                <ul className="mt-2 grid grid-cols-2 gap-1 sm:grid-cols-3">
                  {serviceMenu.map((item) => (
                    <li key={item.href}>
                      <NavLink
                        to={item.href}
                        end={item.href === "/services"}
                        onClick={() => setMenuOpen(false)}
                        className={({ isActive }) =>
                          [
                            "block rounded-md px-3 py-2.5 font-label text-sm font-medium transition-colors",
                            isActive
                              ? "bg-primary-50 text-primary-700"
                              : "text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700",
                          ].join(" ")
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <Button to="/appointment" size="md" icon="ri-calendar-check-line" fullWidth>
                  Book Appointment
                </Button>
                <Button href={clinicInfo.phoneHref} variant="outline" size="md" icon="ri-phone-fill" fullWidth>
                  Call {clinicInfo.phoneDisplay}
                </Button>
              </div>
            </nav>
          </div>
        ) : null}
      </header>
    </>
  );
}